/**
 * All available notes in standard tuning
 * @type {string[]}
 */
export const NOTES = ['A', 'A#', 'B', 'C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#'];

/**
 * Standard guitar tuning from lowest to highest string
 * @type {string[]}
 */
export const STANDARD_TUNING = ['E2', 'A2', 'D3', 'G3', 'B3', 'E4'];

/**
 * Musical modes and their characteristics
 * @type {Object.<string, {name: string, description: string, intervals: string[], usage?: string}>}
 */
export const MODES = {
  major: {
    name: 'Major Scale (Ionian)',
    description: 'The foundation of Western music, with a bright and stable sound.',
    intervals: ['1', '2', '3', '4', '5', '6', '7'],
    usage: 'Happy, triumphant, or resolved musical passages',
  },
  minor: {
    name: 'Natural Minor Scale (Aeolian)',
    description: 'The relative minor scale, with a darker, more melancholic sound.',
    intervals: ['1', '2', '♭3', '4', '5', '♭6', '♭7'],
    usage: 'Sad, dramatic, or introspective musical passages',
  },
  'major pentatonic': {
    name: 'Major Pentatonic Scale',
    description: 'A five-note scale derived from the major scale, removing the 4th and 7th degrees.',
    intervals: ['1', '2', '3', '5', '6'],
    usage: 'Folk, country, and blues music; creates a bright, open sound',
  },
  'minor pentatonic': {
    name: 'Minor Pentatonic Scale',
    description: 'A five-note scale derived from the natural minor scale, very common in rock and blues.',
    intervals: ['1', '♭3', '4', '5', '♭7'],
    usage: 'Rock, blues, and metal; perfect for emotional solos',
  },
  ionian: {
    name: 'Ionian Mode (Major Scale)',
    description: 'The foundation of Western music, with a bright and stable sound.',
    intervals: ['1', '2', '3', '4', '5', '6', '7'],
    usage: 'Happy, triumphant, or resolved musical passages',
  },
  dorian: {
    name: 'Dorian Mode',
    description: 'Minor scale with a raised 6th, creating a jazzy, sophisticated sound.',
    intervals: ['1', '2', '♭3', '4', '5', '6', '♭7'],
    usage: 'Jazz, funk, and modern rock; works well over minor chords',
  },
  phrygian: {
    name: 'Phrygian Mode',
    description: 'Minor scale with a lowered 2nd, giving it a Spanish or Middle Eastern flavor.',
    intervals: ['1', '♭2', '♭3', '4', '5', '♭6', '♭7'],
    usage: 'Spanish music, metal, and fusion; creates tension',
  },
  lydian: {
    name: 'Lydian Mode',
    description: 'Major scale with a raised 4th, creating a dreamy, floating quality.',
    intervals: ['1', '2', '3', '#4', '5', '6', '7'],
    usage: 'Film scores, progressive rock; ethereal or mystical passages',
  },
  mixolydian: {
    name: 'Mixolydian Mode',
    description: 'Major scale with a lowered 7th, common in blues and rock.',
    intervals: ['1', '2', '3', '4', '5', '6', '♭7'],
    usage: 'Blues, rock, and Celtic music; dominant chord soloing',
  },
  aeolian: {
    name: 'Aeolian Mode (Natural Minor)',
    description: 'The relative minor scale, with a darker, more melancholic sound.',
    intervals: ['1', '2', '♭3', '4', '5', '♭6', '♭7'],
    usage: 'Sad, dramatic, or introspective musical passages',
  },
  locrian: {
    name: 'Locrian Mode',
    description: 'The most unstable mode, with a lowered 2nd and 5th.',
    intervals: ['1', '♭2', '♭3', '4', '♭5', '♭6', '♭7'],
    usage: 'Metal and contemporary music; creates extreme tension',
  },
};

/**
 * Common chord progressions for each mode
 * @type {Object.<string, string[]>}
 */
export const COMMON_PROGRESSIONS = {
  major: ['I-IV-V', 'ii-V-I', 'I-vi-IV-V'],
  minor: ['i-iv-v', 'i-VI-III-VII', 'i-iv-VI-V'],
  dorian: ['i7-IV7', 'i-IV', 'i-♭VII-IV'],
  phrygian: ['i-♭II', 'i-♭vii-♭vi', 'i-♭II-♭III'],
  lydian: ['I-II', 'I-V-II', 'I-II-vii°'],
  mixolydian: ['I-♭VII', 'I-♭VII-IV', 'I-v-♭VII'],
  locrian: ['i°-♭VII', 'i°-♭V', 'i°-IV-♭VII'],
};

/**
 * Scale degrees and their functions
 * @type {Object.<string, {name: string, function: string}>}
 */
export const SCALE_DEGREES = {
  1: { name: 'Tonic', function: 'Home note, most stable' },
  2: { name: 'Supertonic', function: 'Creates motion to mediant or dominant' },
  3: { name: 'Mediant', function: 'Establishes major/minor quality' },
  4: { name: 'Subdominant', function: 'Creates motion away from tonic' },
  5: { name: 'Dominant', function: 'Creates tension, resolves to tonic' },
  6: { name: 'Submediant', function: 'Relative minor/major relationship' },
  7: { name: 'Leading Tone', function: 'Strong resolution to tonic' },
};

/**
 * Get the interval between two notes
 * @param {string} root - The root note
 * @param {string} note - The note to find the interval to
 * @returns {string} The interval name
 */
export function getInterval(root, note) {
  const rootIndex = NOTES.indexOf(root);
  const noteIndex = NOTES.indexOf(note);
  const interval = (noteIndex - rootIndex + 12) % 12;

  const intervalNames = {
    0: 'Root',
    1: 'Minor 2nd',
    2: 'Major 2nd',
    3: 'Minor 3rd',
    4: 'Major 3rd',
    5: 'Perfect 4th',
    6: 'Tritone',
    7: 'Perfect 5th',
    8: 'Minor 6th',
    9: 'Major 6th',
    10: 'Minor 7th',
    11: 'Major 7th',
  };

  return intervalNames[interval];
}

/**
 * Get all notes in a scale
 * @param {string} root - The root note
 * @param {string} mode - The mode/scale type
 * @returns {string[]} Array of notes in the scale
 */
export function getScaleNotes(root, mode) {
  if (!MODES[mode]) return [];

  const rootIndex = NOTES.indexOf(root);
  const intervals = MODES[mode].intervals;
  const intervalMap = {
    1: 0,
    2: 2,
    3: 4,
    4: 5,
    5: 7,
    6: 9,
    7: 11,
    '♭2': 1,
    '♭3': 3,
    '#4': 6,
    '♭5': 6,
    '♭6': 8,
    '♭7': 10,
  };

  return intervals.map((interval) => {
    const noteIndex = (rootIndex + intervalMap[interval]) % 12;
    return NOTES[noteIndex];
  });
}

/**
 * Get the full name of an interval
 * @param {string} interval - The interval symbol (e.g., '1', '♭3', '#4')
 * @returns {string} The full interval name
 */
export function getIntervalName(interval) {
  const intervalMap = {
    '1': 'Unison',
    '♭2': 'Minor 2nd',
    '2': 'Major 2nd',
    '♭3': 'Minor 3rd',
    '3': 'Major 3rd',
    '4': 'Perfect 4th',
    '#4': 'Augmented 4th',
    '♭5': 'Diminished 5th',
    '5': 'Perfect 5th',
    '#5': 'Augmented 5th',
    '♭6': 'Minor 6th',
    '6': 'Major 6th',
    '♭7': 'Minor 7th',
    '7': 'Major 7th',
    '8': 'Octave'
  };

  return intervalMap[interval] || interval;
}
